import { Fragment, type MouseEvent, type ReactNode } from 'react'
import { MoreVertical } from 'lucide-react'
import { PreviewPanelToggle } from '@/components/integrations/_shared/preview-panel-toggle'
import { IconButton } from '@/components/ui/icon-button'

/**
 * Right end of an entity or folder title row, in a fixed order:
 * surface actions, then the one right-pane toggle (PanelRight), then `...`.
 * Separators only between visible controls.
 *
 * The pane toggle replaces the document Info button and the Basecamp todo
 * "Edit" button; only `paneLabel` changes per surface. `onMore` opens the
 * same item source as that entity's right-click (ADR-0020).
 * Gallery: /?test=maater-ui#panel-collapse
 */
export function TitleRowTrailing({
  actions = [],
  pane,
  onMore,
  moreTestId,
}: {
  /** Keyed nodes (Save, Reload). */
  actions?: ReactNode[]
  pane?: {
    open: boolean
    onToggle: () => void
    /** "preview panel" (index), "details" (document), "todo editor". */
    paneLabel?: string
    disabledReason?: string
    testId?: string
  }
  onMore?: (event: MouseEvent<HTMLElement>) => void
  moreTestId?: string
}) {
  const controls: ReactNode[] = [...actions]
  if (pane) {
    controls.push(
      <PreviewPanelToggle
        key="shell-pane"
        open={pane.open}
        onToggle={pane.onToggle}
        paneLabel={pane.paneLabel}
        disabledReason={pane.disabledReason}
        testId={pane.testId}
      />,
    )
  }
  if (onMore) {
    controls.push(
      <IconButton
        key="shell-more"
        icon={<MoreVertical className="h-4 w-4" />}
        label="More options"
        variant="ghost"
        size="sm"
        data-testid={moreTestId}
        onClick={onMore}
      />,
    )
  }
  return (
    <div className="flex shrink-0 items-center">
      {controls.map((control, index) => (
        <Fragment key={index}>
          {index > 0 && <div className="mx-1 h-4 w-px bg-border" aria-hidden />}
          {control}
        </Fragment>
      ))}
    </div>
  )
}
